import styled, { keyframes } from 'styled-components';
import { Input, Table } from 'reactstrap';

export const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const UsersContainer = styled.div`
  padding: 20px;
  animation: ${fadeIn} 0.5s ease-out;
`;

export const StyledButton = styled.button`
  padding: 10px 10px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 20px;
  margin-right: 10px;
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #0056b3, #007bff);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 123, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: linear-gradient(135deg, #6c757d, #5a6268);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

export const StyledTableHeader = styled.th`
  background-color: #f0f0f0;
  color: #333;
  font-weight: 600;
`;

export const HeaderButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  .buttons-and-search {
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

export const FilterInput = styled(Input)`
  width: 300px;
  padding: 8px;
  font-size: 16px;
  border: 2px solid #007bff;
  border-radius: 5px;
  outline: none;

  &:focus {
    border-color: #0056b3;
    box-shadow: 0 0 5px rgba(0, 91, 187, 0.5);
  }
`;

export const TableContainer = styled.div`
  position: relative;
  max-height: 80vh;
  overflow-y: auto;
`;

export const StyledTable = styled(Table)`
  width: 100%;
  border-collapse: collapse;

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    border-bottom: 2px solid #ccc;
    padding: 8px;
    text-align: left;
  }

  tbody tr:nth-child(odd) {
    background-color: #f0f8ff;
  }

  tbody tr:nth-child(even) {
    background-color: white;
  }

  tbody td {
    padding: 8px;
    max-width: 200px;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    border: 0.5px solid #ccc;
  }
`;

export const RoleBadge = styled.span<{ role: number }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background: ${props => {
    if (props.role === 0) return 'linear-gradient(135deg, #dc3545, #c82333)'; // Admin - Red
    if (props.role === 1) return 'linear-gradient(135deg, #28a745, #20c997)'; // Teacher - Green
    return 'linear-gradient(135deg, #007bff, #0056b3)'; // Student - Blue
  }};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const WarningButton = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg, #ffc107, #ff9800);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(255, 193, 7, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #ff9800, #ffc107);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(255, 193, 7, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const DangerButton = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #c82333, #dc3545);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(220, 53, 69, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const SuccessButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #20c997, #28a745);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(40, 167, 69, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: linear-gradient(135deg, #6c757d, #5a6268);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

export const SecondaryButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #6c757d, #545b62);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(108, 117, 125, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #545b62, #6c757d);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(108, 117, 125, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const UserCountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 1em;
  user-select: none;

  .count-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.05);
      background: rgba(0, 0, 0, 0.08);
    }
  }

  .admin { color: #dc3545; }
  .teacher { color: #28a745; }
  .student { color: #007bff; }
`;
